import { ChangeEvent, FC, useRef, useState } from 'react';
import { Alert, Box, Button, FormControlLabel, Radio, RadioGroup, Typography } from '@mui/material';
import {
  BulkContractMigrationResponse,
  ContractMigrationMode,
  downloadBulkContractMigrationTemplate,
  uploadBulkContractMigration
} from 'services/contracts';

const MODE_DESCRIPTIONS: Record<ContractMigrationMode, string> = {
  UPSERT:
    'Updates contracts matching an existing customer, size type, and start date within each location, adds new rows, and truncates or removes overlapping schedules so the uploaded rates apply.',
  REPLACE:
    'Deletes ALL contracts only at the locations present in the file, then imports those rows. Locations absent from the file are untouched. This is destructive and cannot be undone.'
};

const splitCsvLine = (line: string): string[] => {
  const values: string[] = [];
  let value = '';
  let quoted = false;
  for (let i = 0; i < line.length; i += 1) {
    const character = line[i];
    if (quoted) {
      if (character === '"' && line[i + 1] === '"') {
        value += '"';
        i += 1;
      } else if (character === '"') {
        quoted = false;
      } else {
        value += character;
      }
    } else if (character === '"') {
      quoted = true;
    } else if (character === ',') {
      values.push(value);
      value = '';
    } else {
      value += character;
    }
  }
  values.push(value);
  return values;
};

export const parseLocationCodes = async (file: File): Promise<string[]> => {
  const text = (await file.text()).replace(/^\uFEFF/, '');
  const lines = text.split(/\r?\n/).filter(line => line.trim().length > 0);
  if (lines.length === 0) return [];
  const header = splitCsvLine(lines[0]).map(value => value.trim());
  const locationIndex = header.indexOf('LocationCode');
  if (locationIndex < 0) return [];
  const codes = new Set<string>();
  for (let i = 1; i < lines.length; i += 1) {
    const fields = splitCsvLine(lines[i]);
    const code = (fields[locationIndex] ?? '').trim();
    if (code) codes.add(code);
  }
  return Array.from(codes).sort();
};

const BulkContractMigrationPage: FC = () => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [mode, setMode] = useState<ContractMigrationMode | null>(null);
  const [confirmingReplace, setConfirmingReplace] = useState(false);
  const [replaceLocations, setReplaceLocations] = useState<string[]>([]);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState<BulkContractMigrationResponse | null>(null);
  const [transportError, setTransportError] = useState<string | null>(null);

  const canSubmit = !!file && !!mode && !uploading;

  const reset = () => {
    setFile(null);
    setMode(null);
    setConfirmingReplace(false);
    setReplaceLocations([]);
    setResult(null);
    setTransportError(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    if (uploading) return;
    const selected = event.target.files?.[0] ?? null;
    setFile(selected);
    setConfirmingReplace(false);
    setReplaceLocations([]);
    setResult(null);
    setTransportError(null);
  };

  const submit = async () => {
    if (!file || !mode || uploading) return;
    setUploading(true);
    setTransportError(null);
    try {
      const response = await uploadBulkContractMigration(mode, file);
      setResult(response);
    } catch (e: any) {
      setTransportError(e?.message || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handlePrimaryAction = async () => {
    if (!mode || !file) return;
    if (mode === 'REPLACE' && !confirmingReplace) {
      const codes = await parseLocationCodes(file);
      setReplaceLocations(codes);
      setConfirmingReplace(true);
      return;
    }
    await submit();
  };

  return (
    <Box sx={{ p: 3, maxWidth: 900 }}>
      <Typography variant="h5" sx={{ mb: 1 }}>
        Contract Migration
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Import contracts for many locations from one CSV. The file must include a{' '}
        <strong>LocationCode</strong> column before the existing 22 contract headers. Each row&apos;s customer
        must exist at that location.
      </Typography>

      <Button variant="outlined" onClick={downloadBulkContractMigrationTemplate} sx={{ mb: 3 }}>
        Download CSV Template
      </Button>

      <Box sx={{ mb: 3 }}>
        <input
          ref={fileInputRef}
          type="file"
          accept=".csv"
          hidden
          onChange={handleFileChange}
          data-testid="bulk-contract-file-input"
        />
        <Button variant="outlined" disabled={uploading} onClick={() => fileInputRef.current?.click()}>
          {file ? file.name : 'Choose CSV File'}
        </Button>
      </Box>

      <Typography variant="subtitle1" sx={{ mb: 1 }}>
        Mode
      </Typography>
      <RadioGroup
        value={mode}
        onChange={event => {
          if (uploading) return;
          setMode(event.target.value as ContractMigrationMode);
          setConfirmingReplace(false);
          setReplaceLocations([]);
        }}
      >
        <FormControlLabel value="UPSERT" control={<Radio />} label="Upsert" disabled={uploading} />
        <FormControlLabel value="REPLACE" control={<Radio />} label="Replace all for file locations" disabled={uploading} />
      </RadioGroup>
      {mode && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {MODE_DESCRIPTIONS[mode]}
        </Typography>
      )}

      {confirmingReplace && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          <Typography variant="subtitle2">Replace contracts for these locations?</Typography>
          <Typography variant="body2">{replaceLocations.join(', ') || 'No locations found in the file'}</Typography>
        </Alert>
      )}

      <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
        <Button
          variant="contained"
          disabled={!canSubmit}
          onClick={handlePrimaryAction}
          data-testid="bulk-contract-submit"
        >
          {uploading
            ? 'Uploading...'
            : mode === 'REPLACE' && !confirmingReplace
            ? 'Review Replace'
            : confirmingReplace
            ? 'Confirm Replace'
            : 'Upload'}
        </Button>
        <Button variant="outlined" disabled={uploading} onClick={reset}>
          Clear
        </Button>
      </Box>

      {transportError && (
        <Alert severity="error" sx={{ mt: 3 }}>
          {transportError}
        </Alert>
      )}

      {result && (
        <Box sx={{ mt: 3 }}>
          {result.recordsFailed > 0 ? (
            <Alert severity="error">
              {result.recordsFailed} of {result.recordsRead} row
              {result.recordsFailed === 1 ? '' : 's'} failed. No changes were written.
            </Alert>
          ) : (
            <Alert severity="success">
              Imported {result.recordsImported} of {result.recordsRead} rows ({result.mode}).
            </Alert>
          )}

          {result.locations.length > 0 && (
            <Box mt={2}>
              <Typography variant="subtitle2">By location</Typography>
              {result.locations.map(location => (
                <Typography key={location.locationId} variant="body2">
                  {location.locationCode}: {location.recordsImported}/{location.recordsRead} imported,{' '}
                  {location.recordsFailed} failed
                </Typography>
              ))}
            </Box>
          )}

          {result.errors.length > 0 && (
            <Box mt={2}>
              <Typography variant="subtitle2">Row errors</Typography>
              <ul>
                {result.errors.map((error, index) => (
                  <li key={`${error.rowNumber}-${index}`}>
                    Row {error.rowNumber ?? 0}
                    {error.locationCode ? ` · Location ${error.locationCode}` : ''}
                    {error.accountCode ? ` · Customer ${error.accountCode}` : ''}: {error.message}
                  </li>
                ))}
              </ul>
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
};

export default BulkContractMigrationPage;
